import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getMemoryGaps, type MemoryGap } from '../services/memory-api.js';

type GapStatus = 'open' | 'resolved' | 'archived';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

@customElement('cp-memory-gaps')
export class CpMemoryGaps extends LitElement {
  createRenderRoot() { return this; }

  @state() private _status: GapStatus = 'open';
  @state() private _gaps: MemoryGap[] = [];
  @state() private _loading = false;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const res = await getMemoryGaps(this._status);
      this._gaps = res.gaps;
    } catch (e) {
      this._error = (e as Error).message;
    } finally {
      this._loading = false;
    }
  }

  private _switchStatus(s: GapStatus): void {
    this._status = s;
    void this._load();
  }

  render() {
    return html`
      <div class="cp-memory-gaps">
        <div class="cp-memory-gaps__header">
          <h4 class="cp-memory-gaps__title">Wissens-Lücken</h4>
          <div class="cp-memory-gaps__tabs">
            ${(['open', 'resolved', 'archived'] as GapStatus[]).map(s => html`
              <button
                class="cp-memory-gaps__tab ${this._status === s ? 'cp-memory-gaps__tab--active' : ''}"
                @click=${() => this._switchStatus(s)}
              >${s}</button>
            `)}
          </div>
        </div>

        ${this._error
          ? html`<div class="cp-memory-gaps__error">${this._error}</div>`
          : nothing}

        ${this._loading
          ? html`<div class="cp-memory-gaps__loading">Laedt…</div>`
          : this._renderList()}
      </div>
    `;
  }

  private _renderList() {
    if (this._gaps.length === 0) {
      return html`<div class="cp-memory-gaps__empty">Keine Lücken mit Status "${this._status}".</div>`;
    }
    return html`
      <table class="cp-memory-gaps__table">
        <thead>
          <tr>
            <th>Keyword</th>
            <th>Agent</th>
            <th>Häufigkeit</th>
            <th>Zuletzt</th>
            <th>Seit</th>
          </tr>
        </thead>
        <tbody>
          ${this._gaps.map(g => html`
            <tr class="cp-memory-gaps__row">
              <td class="cp-memory-gaps__keyword">${g.domain_keyword}</td>
              <td>${g.agent_id ?? '—'}</td>
              <td class="cp-memory-gaps__count">${g.count}</td>
              <td>${fmtDate(g.last_seen_at)}</td>
              <td>${fmtDate(g.first_seen_at)}</td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-memory-gaps': CpMemoryGaps;
  }
}
